// FrozenRegradeStockForm.js
import React from "react";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import TextField from "@mui/material/TextField";
import { getAPIData, postApi } from "../../../utils/apiFunction";
import { textfieldDateFormat } from "../../../utils/dateFunctions";
import OystersGroup from "../../../components/OysterGroup";
import Div from "@jumbo/shared/Div";
import Typography from "@mui/material/Typography";
import SubmitButtonDisplayer from "../../../components/SubmitButtonDisplayer";
import useSwalWrapper from "@jumbo/vendors/sweetalert2/hooks";
import { ACCESS, ACCESS_SECTIONS } from "../../../utils/constants/access";
import { checkAccess } from "../../../utils/roleFunctions";
import SelectTextField from "../../../components/SelectTextField";

// ✅ Use the same saveLabel used by the manual Frozen Labeler
import { saveLabel } from "../../../utils/fulfilment/labels";

// --- helpers (local only for UI) ---
const addYears = (d, n = 1) => {
  const x = new Date(d);
  x.setFullYear(x.getFullYear() + n);
  return x;
};
const ddmmyyyy = (d) =>
  `${String(d.getDate()).padStart(2, "0")}/${String(d.getMonth() + 1).padStart(
    2
  )}/${d.getFullYear()}`;

const FrozenRegradeStockForm = (props) => {
  const [completed, setCompleted] = React.useState(props.completed);
  const [updateComplete, setUpdateCompleted] = React.useState(false);
  const [edit] = React.useState(props.edit);
  const [error, setError] = React.useState(null);
  const [loading, setLoading] = React.useState(false);
  const [stockItems, setStockItems] = React.useState(null);
  const [comment, setComment] = React.useState(null);
  const [outputStockItem, setOutputStockItem] = React.useState(null);
  const Swal = useSwalWrapper();

  const requiredAdminAccess = {
    key: ACCESS_SECTIONS.PROCESSING,
    value: ACCESS.EDIT,
  };

  const CompletedAndEdit = edit && completed;
  const CompletedSoDisabled = !edit && completed;

  const supplier = props.selectedLine?.supplier || "";
  const lotNbr = props.selectedLine?.lotSerialNbr || "";

  const [oystersIn, setOystersIn] = React.useState([
    {
      bags: props.selectedLine.bags,
      bins: props.selectedLine.bins,
      stockItemId: props.selectedLine.stockItemId,
      quantity: props.selectedLine.amount,
    },
  ]);

  React.useEffect(() => {
    getAPIData("/StockItems/GetProcessed", setStockItems);
  }, []);

  const validate = () => () => {
    const errs = [];
    if (props.selectedLine.amount < (oystersIn?.[0]?.quantity || 0)) {
      errs.push(
        "You have entered more oysters than are available in the Inventory"
      );
    }
    if (!oystersIn?.[0]?.quantity || oystersIn?.[0]?.quantity <= 0) {
      errs.push("You must enter a quantity of oysters for input");
    }
    if (!oystersIn?.[0]?.stockItem) {
      errs.push("You must select a stockitem for input");
    }
    if (!outputStockItem || !outputStockItem.id) {
      errs.push("Please select a valid output stock item");
    }
    if (errs.length) {
      Swal.fire({ html: errs.join("<br/>"), icon: "error", title: "Oops..." });
      return;
    }
    submit();
  };

  const submit = async () => {
    setLoading(true);

    const params = {
      lotSerNbr: props.selectedLine.lotSerialNbr,
      ExpiryDate: textfieldDateFormat(new Date(props.selectedLine.expiryDate)),
      OriginalStockItemId: oystersIn[0].stockItem.id,
      NewStockItemId: outputStockItem.id,
      WarehouseId: props.selectedLine.warehouseId,
      Amount: oystersIn[0].quantity,
    };

    const respArr = [];
    respArr.push(await postApi("/Processing/Regrade", params));
    const ok = respArr.reduce((p, c) => p && c?.ok, true);

    if (ok) {
      setCompleted(true);
      setUpdateCompleted(true);
      setLoading(false);

      // === Build payload EXACTLY like manual Frozen Labeler ===
      // Best Before (manual frozen = print date + 1 year)
      const bestBeforeYMD = (() => {
        const d = new Date();
        d.setFullYear(d.getFullYear() + 1);
        return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}-${String(d.getDate()).padStart(2, "0")}`;
      })();

      // --- Species-based default quantity rule ---
      // Pacific => 10 dozen; Sydney Rock => 25 dozen (fallback 25)
      const isPacific =
        !!outputStockItem?.isPacific ||
        (typeof outputStockItem?.species?.name === "string" &&
          outputStockItem.species.name.toLowerCase().includes("pacific")) ||
        (typeof outputStockItem?.description === "string" &&
          outputStockItem.description.toLowerCase().includes("pacific"));

      const defaultDozens = isPacific ? 10 : 25;

      // detailsList: includes ivn with lotSerialNbr, estuary, expiryDate AND stockItem
      const detailsList = [
        {
          ivn: {
            lotSerialNbr: props.selectedLine.lotSerialNbr,
            estuary:
              props.selectedLine?.estuary ||
              props.selectedLine?.supplierEstuary ||
              "",
            expiryDate: props.selectedLine.expiryDate,
            stockItem: outputStockItem, // important for template expectations
          },
          quantity: defaultDozens, // ✅ species-based default
        },
      ];

      // codeInfo: mirror modal’s object; include frozen flag and best-before fields
      const codeInfo = {
        ...outputStockItem,
        isFrozen: true,
        isPacific, // ✅ expose to template/label builder if used
        BestBefore: bestBeforeYMD,
        bestBefore: bestBeforeYMD,
        bbDate: bestBeforeYMD,
        bagCount: defaultDozens, // ✅ keep bags/dozens consistent
      };

      // saleItem not used for frozen regrade (no address block)
      const saleItem = { customer: {} };

      // extra tells template this is frozen modal-style (use-by = print date + 1 year)
      const extra = {
        context: "frozen_labeler",
        printDate: new Date().toISOString(),
      };

      try {
        Swal.fire({
          icon: "success",
          title: "Regrade complete",
          text: "Printing label…",
          timer: 1400,
          showConfirmButton: false,
        });

        // EXACT same signature/path as manual flow; do NOT force units here.
        await saveLabel(
          detailsList,
          saleItem,
          codeInfo,
          false, // halfshell
          null,  // forceUnits
          null,  // comboPRN
          extra
        );

        // Close after a moment to let the print window appear
        setTimeout(() => props.handleClose?.(), 600);
      } catch (e) {
        console.error("Label print failed:", e);
        Swal.fire({
          icon: "warning",
          title: "Regrade done",
          text: "Label was created, but the print window didn’t open automatically.",
        });
        setTimeout(() => props.handleClose?.(), 600);
      }
    } else {
      setLoading(false);
      setError("Failed to submit");
      Swal.fire({
        icon: "error",
        title: "Regrade failed",
        text: "The server did not confirm the change.",
      });
    }
  };

  const remSR = (name) => {
    if (name && name.description)
      return name.description.replace("Sydney Rock", "");
    return "";
  };

  return (
    <Box
      component="form"
      sx={{ "& .MuiTextField-root": { m: 1, width: "24ch" } }}
      noValidate
      autoComplete="off"
    >
      <Div>
        <TextField disabled label="Supplier" value={supplier} />
        <TextField disabled label="Lot Nbr" value={lotNbr} />
      </Div>

      <Div>
        <Grid container spacing={2} mb={1}>
          <Grid item xs={12}>
            <TextField
              disabled={CompletedSoDisabled}
              label="Comment"
              value={comment ?? ""}
              onChange={(event) => {
                setComment(event.target.value);
              }}
              multiline
              InputLabelProps={{ shrink: true }}
              rows={4}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant={"h3"}>{"Input Stock"}</Typography>
            <OystersGroup
              processed={props.processed}
              disableStockItem
              oysters={oystersIn}
              starter={false}
              setOysters={setOystersIn}
              disabled={CompletedSoDisabled}
              single
              noTitle
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant={"h3"}>{"Output Stock"}</Typography>
            <SelectTextField
              key={"si" + props.index}
              title="Stock Item"
              state={outputStockItem}
              nameFunc={remSR}
              list={stockItems?.filter((si) => {
                const sameSpecies =
                  si?.species?.id === oystersIn?.[0]?.stockItem?.species?.id;
                return sameSpecies && si?.isFrozen; // Frozen only
              })}
              setState={setOutputStockItem}
              disabled={props.disabled}
            />
          </Grid>

          {CompletedAndEdit ? (
            <Grid item xs={12}>
              <div style={{ paddingTop: "5px", textAlign: "left" }}>
                <SubmitButtonDisplayer
                  title={"Update"}
                  completed={updateComplete}
                  error={error}
                  loading={loading}
                  allowed={checkAccess(props.access, requiredAdminAccess)}
                  submitFunc={validate(false)}
                  handleClose={props.handleClose}
                />
              </div>
            </Grid>
          ) : (
            <Grid item xs={12} sm={12} md={6} lg={6} xl={6}>
              <div style={{ paddingTop: "5px", textAlign: "left" }}>
                <SubmitButtonDisplayer
                  title={"Regrade"}
                  completed={CompletedSoDisabled}
                  error={error}
                  loading={loading}
                  allowed={checkAccess(props.access, requiredAdminAccess)}
                  submitFunc={validate(false)}
                  handleClose={props.handleClose}
                />
              </div>
            </Grid>
          )}
        </Grid>
      </Div>
    </Box>
  );
};

export default FrozenRegradeStockForm;
